import React from 'react';
import { AppBar, Toolbar, Container, Box } from '@mui/material';
import PropTypes from 'prop-types';

import { Logo } from './components/logo';
import { PagesNavigation } from './components/pages-navigation';
import { MessageNotification } from './components/message-notification';
import { UserMenu } from './components/nav-menu/user-menu';
import { DashboardMenu } from './components/nav-menu/dashboard-menu';
import { LoggedInUserPropType } from '@src/components/prop-types/user';
import { useTwilioChannelUnreadSubscriber } from '@src/lib/twilio-chat/useTwilioChannelUnreadSubscriber';

const baseNavPages = [
    { id: 'find-a-task', label: 'Find a tasker', href: '/search' },
    { id: 'provide-a-task', label: 'Provide a task', href: '/pages/provide-a-task' },
    { id: 'about', label: 'About', href: '/pages/about' },
    { id: 'faq', label: 'FAQ', href: 'https://bcaa.com/tm-faq', isExternal: true },
    { id: 'contact-us', label: 'Contact Us', href: 'https://www.bcaa.com/bcaa-marketplace/task-marketplace/contact-us' }
];

const VerticalDivider = ({ user }) => (
    <Box
        sx={{
            height: 32,
            borderLeft: '1px solid white',
            ...(user
                ? {
                      mx: 1
                  }
                : {
                      mx: 2
                  }),
            display: user && !user.Guest ? 'block' : { xs: 'none' }
        }}
    />
);

export function ResponsiveAppBar({
    user,
    unreadCount,
    signOut,
    isPrivateEnabled,
    isMerchantRestrictedOnly,
    getUnreadCount
}) {
    const isMerchant = user?.Roles?.includes('Merchant');
    const privateMerchantRestricted = isPrivateEnabled || isMerchantRestrictedOnly;
    const becomeATaskerLink = privateMerchantRestricted ? '/accounts/sign-in' : '/accounts/non-private/be-seller';

    useTwilioChannelUnreadSubscriber({
        getUnreadCount: getUnreadCount,
        userEmail: user?.Email
    });

    const navPages = React.useMemo(() => {
        if (user && !user.Guest && !isMerchant) {
            return [
                ...baseNavPages,
                {
                    id: 'become-a-tasker',
                    label: 'Become a Tasker',
                    href: becomeATaskerLink
                }
            ];
        }
        return baseNavPages;
    }, [isMerchant, becomeATaskerLink]);

    return (
        <AppBar
            position='static'
            sx={{
                backgroundColor: '#0050C4',
                height: '50px',
                justifyContent: 'center',
                boxShadow: 'none',
                fontSize: '14px',
                color: 'white'
            }}
        >
            <Container maxWidth='xl' sx={{ desktop: { px: '40px' } }}>
                <Toolbar
                    disableGutters
                    sx={{
                        height: '70px',
                        minHeight: '70px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '60px'
                    }}
                >
                    <Logo />

                    <PagesNavigation pages={navPages} />

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {user && !user.Guest && (
                            <React.Fragment>
                                <MessageNotification unreadCount={unreadCount} />

                                {isMerchant && (
                                    <React.Fragment>
                                        <VerticalDivider user={user} />
                                        <DashboardMenu user={user} />
                                    </React.Fragment>
                                )}
                            </React.Fragment>
                        )}

                        <VerticalDivider user={user} />

                        <UserMenu
                            navPages={navPages}
                            user={user}
                            isMerchant={isMerchant}
                            signOut={signOut}
                            isPrivateEnabled={isPrivateEnabled}
                            isMerchantRestrictedOnly={isMerchantRestrictedOnly}
                        />
                    </Box>
                </Toolbar>
            </Container>
        </AppBar>
    );
}

VerticalDivider.propTypes = {
    user: LoggedInUserPropType
};

ResponsiveAppBar.propTypes = {
    user: LoggedInUserPropType,
    unreadCount: PropTypes.number,
    signOut: PropTypes.func.isRequired,
    isPrivateEnabled: PropTypes.bool,
    isMerchantRestrictedOnly: PropTypes.bool,
    getUnreadCount: PropTypes.func
};
